import { useEffect, useRef, type CSSProperties } from 'react';
import { OntologyDesigner } from './editor/components/OntologyDesigner';
import { useAppStore, themeClass, type ThemeId } from './editor/store/appStore';
import { GitLabBar } from './components/GitLabBar';
import type { WitboostTheme } from './index';
import './editor/styles/app.css';
import './ontologyEditor.css';

interface Props {
  theme?: WitboostTheme;
  entity: unknown;
  token: string;
}

type Palette = {
  type?: string;
  mode?: string;
  primary?: { main?: string; dark?: string };
};

function getPalette(theme?: WitboostTheme): Palette {
  return (theme?.palette as Palette | undefined) ?? {};
}

function resolveThemeId(theme?: WitboostTheme): ThemeId {
  const palette = getPalette(theme);
  return (palette.type ?? palette.mode) === 'dark' ? 'dark' : 'light';
}

// Witboost hands us a Material UI theme object we cannot consume as a theme, so
// the few tokens worth honouring are pushed in as CSS custom properties.
function themeVariables(theme?: WitboostTheme): CSSProperties {
  const palette = getPalette(theme);
  const fontFamily = (theme?.typography as { fontFamily?: string } | undefined)?.fontFamily;

  return {
    ...(palette.primary?.main ? { '--ms-blue': palette.primary.main } : {}),
    ...(palette.primary?.dark ? { '--ms-blue-dark': palette.primary.dark } : {}),
    ...(fontFamily ? { '--font-primary': fontFamily } : {}),
  } as CSSProperties;
}

const MIN_HEIGHT_PX = 512;

function px(value: string): number {
  return Number.parseFloat(value) || 0;
}

// Witboost scrolls an inner <main>, not the window, so the viewport is the wrong
// thing to measure against.
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
  }

  return null;
}

// Space the ancestors keep below us, which we would otherwise claim and overflow.
function gapBelow(element: HTMLElement, container: HTMLElement | null): number {
  let gap = 0;

  for (let node: HTMLElement | null = element; node && node !== container; node = node.parentElement) {
    gap += px(getComputedStyle(node).marginBottom);

    if (node.parentElement) {
      gap += px(getComputedStyle(node.parentElement).paddingBottom);
    }
  }

  return gap;
}

/**
 * The host gives the container no height, so `height: 100%` resolves to nothing
 * and the editor collapses to its minimum. We cannot know the host's chrome, so
 * measure where we start, where the scrolling ancestor ends, and claim what is
 * left between the two.
 */
function useFillViewport() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const fit = () => {
      const container = scrollParent(element);
      const bottom = container ? container.getBoundingClientRect().bottom : window.innerHeight;
      const available =
        Math.min(bottom, window.innerHeight) -
        element.getBoundingClientRect().top -
        gapBelow(element, container);

      element.style.height = `${Math.max(available, MIN_HEIGHT_PX)}px`;
    };

    fit();

    // Resize is the one window-level listener worth keeping: it carries no input
    // the host could want, and there is no element-scoped equivalent.
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return ref;
}

function App({ theme, entity }: Props) {
  const setTheme = useAppStore((s) => s.setTheme);
  const themeId = useAppStore((s) => s.theme);
  const rootRef = useFillViewport();

  useEffect(() => {
    setTheme(resolveThemeId(theme));
  }, [theme, setTheme]);

  return (
    <div className="wb-ontology-editor" style={themeVariables(theme)} ref={rootRef}>
      {/* Theme class sits inside the scope root: prefixing makes it a descendant selector. */}
      <div className={`oe-shell ${themeClass(themeId)}`}>
        <GitLabBar entity={entity} />
        <OntologyDesigner />
      </div>
    </div>
  );
}

export default App;
